// Core domain types for the Real-Time Tournament Automation Engine.
// These are framework-free so they can be unit-tested and reused regardless of
// the persistence backend (in-memory now, Postgres later).

export type PlayerId = string; // e.g. "P1"

import type { LanguageCode } from './languages';

/**
 * The kind of organisation a company is, picked at creation. Only Sport Club
 * exists so far; the catalog (and its guards) live in `src/domain/companies.ts`.
 * The value is a stable string code, stored as an enum in Postgres.
 */
export type CompanyType = 'SPORT_CLUB';

/**
 * An organisation on the platform that governs its own tournaments. Every
 * tournament belongs to exactly one company. Companies are managed by the
 * platform administrator; tournaments created before companies existed were
 * migrated onto the default company (see `DEFAULT_COMPANY_ID`). Players are
 * not linked to companies (yet) — anyone can participate in any tournament.
 *
 * Unlike the tournament entities, companies are keyed by a database-assigned
 * integer, not a named-block string id.
 *
 * `type` and `location` are chosen when the company is created and never change
 * afterwards — not even through a profile edit. Existing/default companies may
 * carry no location (`locationId === null`).
 */
export interface Company {
  id: number; // auto-assigned integer primary key
  name: string; // display name shown in the management UI
  type: CompanyType; // kind of organisation (Sport Club); set at creation, immutable
  description: string; // profile text, markdown-formatted ('' = none)
  logoUrl: string | null; // public URL of the uploaded logo (null = none)
  locationId: number | null; // dictionary Location id; set at creation, immutable (null = none)
  defaultLanguage: LanguageCode; // UI language its users see by default
  // Opaque, unguessable capability token for the company's shareable public home
  // page, e.g. "Club_<uuid>" (see `clubHomeToken` in `ids.ts`). Generated
  // server-side at creation, immutable, and never the integer id — the id is a
  // small, guessable number and must not be the capability on a public URL. See
  // `features/club-home.md`.
  homeToken: string;
  // Whether the (always public) home page lists the tournaments this club has
  // organised (each linking to its public spectator view). On by default;
  // company-controlled. Every company has a public home page — there is no
  // private/public flag — so the only home-page preference is this one.
  showTournaments: boolean;
  // When a Super Admin marked the company as verified (epoch ms), or null while
  // it is still under review. A timestamp rather than a boolean, matching the
  // platform's other marks (see `features/soft-delete.md`). Companies created
  // by platform staff are verified from the start; a company that signed itself
  // up through the public wizard stays unverified — unable to add athletes,
  // coaches or users — until a Super Admin verifies it (see
  // `features/company-registration.md`).
  verifiedAt: number | null;
  createdAt: number; // creation time (epoch ms)
}

/**
 * Per-UI-language display names for a location, keyed by supported language
 * code. Partial: a missing language falls back to the canonical `name` (see
 * `localizedLocationName` in `src/domain/locations.ts`).
 */
export type LocationTranslations = Partial<Record<LanguageCode, string>>;

/**
 * An entry in the platform's location dictionary — the closed set of places a
 * company picks its home city from at creation. Keyed by an auto-assigned
 * integer, like `Company`. `name` is the canonical (English) display name;
 * `translations` overrides it per UI language; `latitude`/`longitude` are its
 * WGS84 map coordinates. The dictionary is seeded by a migration (and
 * self-healed at boot); it is read-only at runtime.
 */
export interface Location {
  id: number; // auto-assigned integer primary key
  name: string; // canonical (English) display name, e.g. "Irkutsk"
  translations: LocationTranslations; // per-language name overrides
  country: string; // canonical (English) country name, e.g. "Russia" ('' = unknown)
  latitude: number; // WGS84 latitude (degrees)
  longitude: number; // WGS84 longitude (degrees)
  createdAt: number; // creation time (epoch ms)
}

/**
 * The full, server-internal representation of a company profile photo. Uploaded
 * by a company admin and processed (auto-orient → resize → WebP, with NO
 * watermark) exactly like a tournament photo — but delivered synchronously, so
 * only the public WebP is kept: there is no private original and no async
 * processing state. `webpObject` is a storage key, never exposed to a client.
 * Map to `CompanyPhoto` before returning to a browser.
 */
export interface StoredCompanyPhoto {
  id: string; // Photo_<uuid>
  companyId: number; // owning company
  webpObject: string; // storage key of the processed WebP (server-internal)
  webpUrl: string; // public URL of the processed WebP
  width: number; // processed pixel width (post-orientation)
  height: number; // processed pixel height (post-orientation)
  orientation: PhotoOrientation; // aspect class of the processed image
  createdAt: number; // upload time (epoch ms)
}

/**
 * Client-facing view of a company profile photo (the storage key dropped).
 * Carries only what the profile gallery needs to render an orientation-aware,
 * downloadable image.
 */
export interface CompanyPhoto {
  id: string;
  url: string; // public WebP URL
  width: number;
  height: number;
  orientation: PhotoOrientation;
  createdAt: number;
}

/**
 * A single grantable capability, identified by a stable string code. The full
 * catalog (and the predefined roles bundling them) lives in
 * `src/domain/users.ts`; the codes are primary keys in Postgres.
 */
export type PermissionCode =
  | 'CREATE_TOURNAMENT'
  | 'DELETE_TOURNAMENT'
  | 'MANAGE_PHOTOS'
  | 'CREATE_COMPANY'
  | 'VERIFY_COMPANY'
  | 'EDIT_COMPANY'
  | 'CREATE_USER'
  | 'DELETE_USER'
  | 'EDIT_USER'
  | 'MANAGE_ATHLETES'
  | 'MANAGE_ATHLETE_ACCESS'
  | 'MANAGE_TEAMS'
  | 'MANAGE_TRAINING_CLASSES';

/** A permission's catalog entry, as listed in the management UI. */
export interface PermissionInfo {
  id: PermissionCode;
  name: string; // human label, e.g. "Create tournament"
  description: string;
}

/**
 * A named bundle of permissions. Keyed by a stable string code (e.g.
 * `SUPER_ADMIN`) so predefined roles can be referenced from code. What a user
 * may do is the union of their roles' permissions and their direct grants.
 */
export interface RoleInfo {
  id: string;
  name: string; // human label, e.g. "Super Admin"
  description: string;
  permissionIds: PermissionCode[];
}

/**
 * A platform user as exposed to the management UI — everything about the
 * account except its password hash, which never leaves the server. `roleIds`,
 * `permissionIds` (direct grants on top of roles) and `companyIds` (the
 * companies the user may act on; ignored for Super Admins, who access all)
 * mirror the `UserRole` / `UserPermission` / `UserCompany` join tables.
 */
export interface UserAccount {
  id: string;
  email: string;
  name: string;
  // Preferred UI / email language (a supported `LanguageCode`), or null to
  // follow their company default. Drives which per-language transactional
  // template the passwordless-login email uses (see
  // `features/passwordless-login.md`).
  language: LanguageCode | null;
  // When the account was blocked (epoch ms); null = active. A blocked user
  // cannot log in and their tokens stop working. A date rather than a boolean,
  // like the soft-delete marks (see `features/soft-delete.md`).
  blockedAt: number | null;
  createdAt: number; // creation time (epoch ms)
  roleIds: string[];
  permissionIds: PermissionCode[];
  companyIds: number[];
}

/**
 * The signed-in user's own account plus the *derived* access picture the UI
 * renders from: the effective permission union and whether company scoping is
 * bypassed entirely (Super Admin). Returned by `GET /api/auth/me`.
 */
export interface CurrentUser {
  user: UserAccount;
  permissions: PermissionCode[];
  allCompanies: boolean;
  // The athlete profiles this account reaches as the athlete themselves or as a
  // guardian (see `features/athlete-access.md`). Empty for ordinary staff
  // accounts. Deliberately *not* derived from permissions or company links: an
  // athlete account holds neither, so this is the whole of what it can see.
  athleteLinks: AthleteLinkView[];
}

/**
 * How a user account relates to an athlete profile it can reach: `SELF` is the
 * athlete themselves, `GUARDIAN` is a parent/carer acting for them. The rules
 * live in `src/domain/athleteAccess.ts`.
 */
export type AthleteAccessRelation = 'SELF' | 'GUARDIAN';

/**
 * A live grant of access to one athlete profile (an `AthleteAccess` row; see
 * `features/athlete-access.md`). This is the *only* thing that authorises the
 * athlete-facing surface — an athlete account has no permissions and no company
 * links, so a missing access row means no access at all.
 *
 * `canEdit` covers only the self-editable subset of a profile (photos and bio);
 * identity fields stay club-managed. `revokedAt` is a timestamp, not a boolean,
 * matching the platform's block/delete marks (see `features/soft-delete.md`) —
 * a revoked row is kept for audit and ignored by every read.
 */
export interface AthleteAccessLink {
  id: string; // opaque row id, e.g. "Access_<uuid>"
  personalNumber: string; // the athlete the grant is about
  userId: string; // the account that holds the grant
  relation: AthleteAccessRelation;
  canEdit: boolean;
  // The staff user or guardian who issued the invite this grant came from;
  // null when unknown. Kept as the audit trail of who let this account in —
  // for a child account, the guardian's row *is* the consent record.
  invitedByUserId: string | null;
  createdAt: number; // epoch ms
  revokedAt: number | null; // epoch ms; null = live
}

/**
 * An athlete's membership of one club (an `AthleteClub` row; see
 * `features/athlete-clubs.md`). An athlete trains at one or more clubs: their
 * **home** club owns the profile (`Athlete.companyId`), and further clubs carry
 * them as a guest. Both are rows of this shape — home-ness is derived by
 * comparing `companyId` with the athlete's owning company (`isHomeClub`), never
 * stored twice and so never able to disagree.
 *
 * Leaving stamps `leftAt` rather than deleting, matching the platform's other
 * marks (see `features/soft-delete.md`), so a returning athlete reuses their row
 * and the history survives.
 */
export interface ClubMembershipRecord {
  id: number; // auto-assigned integer primary key
  personalNumber: string; // the member athlete
  companyId: number; // the club
  leftAt: number | null; // epoch ms; null = still a member
  createdAt: number; // epoch ms
}

/**
 * A pending invitation to claim access to an athlete profile (an
 * `AthleteInvite` row). Only the keyed *hash* of the emailed token is ever
 * persisted, exactly like `LoginLinkChallenge` — a database leak cannot mint a
 * live invite. Single-use (`consumedAt`), and possessing the token is itself
 * the proof that the recipient controls `email`, which is why there is no
 * separate email-verification step.
 */
export interface AthleteInviteChallenge {
  id: string; // opaque row id, e.g. "Invite_<uuid>"
  personalNumber: string; // the athlete the invite grants access to
  email: string; // normalized recipient address
  relation: AthleteAccessRelation; // the grant the claim will create
  tokenHash: string; // keyed hash of the emailed token — the only stored form
  expiresAt: number; // epoch ms
  consumedAt: number | null; // epoch ms; null while still claimable
  invitedByUserId: string | null; // staff user (or guardian) who sent it
  createdAt: number; // epoch ms
}

/**
 * One entry of the signed-in account's athlete access picture, as returned by
 * `GET /api/auth/me`. Carries the personal number because the athlete-facing
 * routes are keyed by it and this is the caller's *own* profile (or their
 * child's) — never someone else's. `isMinor` is derived from the date of birth
 * at request time, never stored.
 */
export interface AthleteLinkView {
  accessId: string;
  personalNumber: string;
  fullName: string;
  relation: AthleteAccessRelation;
  canEdit: boolean;
  isMinor: boolean;
}

/**
 * An athlete's gender. Competition categories are gendered, so every athlete
 * profile carries it (required at creation).
 */
export type Gender = 'MALE' | 'FEMALE';

/**
 * An athlete profile owned by exactly one company (only the company creates and
 * manages it; see `features/athletes.md`). Keyed by `personalNumber` — the
 * athlete's platform-wide identity, one allowed letter plus 8 digits (e.g.
 * "H67890954"; see `src/domain/athletes.ts` for the alphabet). An athlete can
 * additionally be marked as a coach, which unlocks a free-text description;
 * companies manage coaches and athletes as separate lists in the UI. Athletes
 * are deliberately not linked to tournament players.
 *
 * The athlete row is pure, sport-agnostic identity: name, gender, date of
 * birth, contact/photos. Everything sport-specific lives in one
 * `SportProfile` per (athlete, sport) — see `features/teams-sports.md`.
 */
export interface Athlete {
  personalNumber: string; // platform-wide primary key, e.g. "H67890954"
  companyId: number; // integer id of the owning company
  fullName: string;
  gender: Gender;
  dateOfBirth: string; // ISO calendar date, "YYYY-MM-DD"; age is derived, never stored
  isCoach: boolean; // marked as coach — unlocks the description
  coachDescription: string; // coach bio ('' when none or not a coach)
  photoUrls: string[]; // public profile photo URLs, at most ATHLETE_PHOTO_MAX
  // Opaque, unguessable capability token for the shareable profile page, e.g.
  // "Profile_<uuid>" (see `athleteProfileToken` in `ids.ts`). Generated
  // server-side at creation, immutable, and never the personal number — the
  // personal number is the platform primary key and must not appear on any
  // public URL. See `features/athlete-profile.md`.
  profileToken: string;
  // Whether the profile is served on the public (unauthenticated) route. A
  // coach's profile is public by default; a plain athlete's is private, so a
  // shared link degrades to 404 until the company enables it. Toggled behind
  // `MANAGE_ATHLETES`.
  profilePublic: boolean;
  createdAt: number; // creation time (epoch ms)
}

/** The raw fields captured by the athlete profile form (create and edit). */
export interface AthleteInput {
  fullName: string;
  // Kept as a plain string at the input boundary; `validateAthlete` narrows it
  // to a known `Gender` (it is required).
  gender?: string;
  dateOfBirth: string;
  isCoach?: boolean;
  coachDescription?: string;
  photoUrls?: string[];
}

/**
 * A sport's stable string code (e.g. "FOOTBALL"), the primary key of the
 * `Sport` config table. The catalog lives in `src/domain/sports.ts`.
 */
export type SportId = string;

/**
 * The spec of one per-athlete attribute a sport declares in its config:
 * either a single choice from a closed list (`enum`, e.g. a football
 * position) or a set of choices (`multiEnum`, e.g. table-tennis formats).
 */
export interface SportAttributeSpec {
  kind: 'enum' | 'multiEnum';
  values: string[];
}

/**
 * A sport's config: which attribute keys a `SportProfile.attributes` object
 * may carry and the valid values of each. Stored as JSONB on the `Sport` row
 * and interpreted by `validateSportAttributes` in `src/domain/sports.ts`.
 */
export interface SportConfig {
  attributes: Record<string, SportAttributeSpec>;
}

/**
 * A sport definition/config row (see `features/teams-sports.md`). Keyed by a
 * stable string code so sports can be referenced from code, mirroring the
 * permission catalog pattern.
 */
export interface Sport {
  id: SportId;
  name: string; // display name, e.g. "Table tennis"
  hasTeams: boolean; // whether the sport is played in teams
  hasWeightCategory: boolean; // whether competition uses weight categories
  config: SportConfig; // valid per-athlete attributes
  createdAt: number; // creation time (epoch ms)
}

/**
 * An athlete's per-sport profile — exactly one per (athlete, sport). All
 * sport-specific attributes live in `attributes`, validated against the
 * parent sport's config; the athlete row stays sport-agnostic identity. A
 * profile is valid standalone (zero memberships).
 */
export interface SportProfile {
  id: number; // auto-assigned integer primary key
  athleteId: string; // the athlete's personal number
  sportId: SportId;
  attributes: Record<string, unknown>; // sport-specific fields, e.g. { position: "CB" }
  createdAt: number; // creation time (epoch ms)
}

/**
 * A team of athletes within a company, always for exactly one sport. A
 * company may span multiple sports via multiple teams.
 */
export interface Team {
  id: number; // auto-assigned integer primary key
  companyId: number; // owning company
  sportId: SportId; // the one sport this team plays
  name: string; // display name, e.g. "First team"
  createdAt: number; // creation time (epoch ms)
}

/**
 * Links a sport profile to a team — the only place `tier` and `teamRole`
 * exist. The profile and the team always share `sportId` (enforced by
 * composite foreign keys in Postgres). An athlete may hold memberships in
 * several teams of the same sport simultaneously (e.g. senior + reserve).
 */
export interface Membership {
  id: number; // auto-assigned integer primary key
  sportProfileId: number;
  teamId: number;
  sportId: SportId; // always equals the profile's and the team's sport
  tier: string; // the athlete's level in that team, e.g. "FIRST_TEAM" ('' = unset)
  teamRole: string; // role actually played there, may differ from the profile's natural position ('' = unset)
  createdAt: number; // creation time (epoch ms)
}

/** The raw fields captured by the sport-profile form (create and edit). */
export interface SportProfileInput {
  // Fixed at creation (one profile per athlete per sport; memberships link
  // through it); ignored by updates.
  sportId?: string;
  // Sport-specific fields, validated against the sport's config.
  attributes?: Record<string, unknown>;
}

/** The raw fields captured by the team form (create and edit). */
export interface TeamInput {
  name: string;
  // Fixed at creation (memberships depend on it); ignored by updates.
  sportId: string;
}

/** The raw fields captured when adding or editing a team membership. */
export interface MembershipInput {
  // The athlete to add; the server resolves their profile for the team's
  // sport. Ignored by updates (the linked profile never changes).
  athleteId?: string;
  tier?: string;
  teamRole?: string;
}

/** A day of the week a class meets, as a stable code (order: Mon…Sun). */
export type Weekday = 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';

/**
 * One weekly-recurring time slot of a training class's schedule: a weekday and
 * a start/end time. Times are 24-hour `"HH:MM"` strings, `endTime` strictly
 * after `startTime`. A class's schedule is an ordered list of these.
 */
export interface TrainingSlot {
  day: Weekday;
  startTime: string; // "HH:MM", 24-hour
  endTime: string; // "HH:MM", 24-hour, strictly after startTime
}

/**
 * Who a training class is meant for: everyone, kids only, or adults only.
 * `ALL` is the default and means the class is open to any age group.
 */
export type TrainingAudience = 'ALL' | 'KIDS' | 'ADULTS';

/**
 * A recurring training class run by a company (see
 * `features/training-classes.md`). A class carries a structured weekly
 * `schedule` (a list of `TrainingSlot`s, `[]` = none), a free-text
 * `description` and an `audience` (kids / adults / open to all), has one or
 * more coaches attached (`TrainingClassCoach`) and athletes enrolled
 * (`TrainingClassEnrollment`), and meets on dated occurrences
 * (`TrainingSession`) whose attendance is tracked (`AttendanceRecord`).
 * Keyed by an auto-assigned integer, like `Team`. Deletion is soft.
 */
export interface TrainingClass {
  id: number; // auto-assigned integer primary key
  companyId: number; // owning company
  name: string; // display name, e.g. "Junior squad"
  description: string; // free-text blurb ('' = none)
  schedule: TrainingSlot[]; // weekly recurring slots, canonically ordered ([] = none)
  audience: TrainingAudience; // who the class is for ('ALL' = everyone)
  createdAt: number; // creation time (epoch ms)
}

/**
 * Attaches a coach to a training class. The coach is an athlete of the same
 * company marked `isCoach`. A class may have several coaches; only the exact
 * (class, athlete) pair is unique among live rows. Deletion is soft.
 */
export interface TrainingClassCoach {
  id: number; // auto-assigned integer primary key
  trainingClassId: number;
  athleteId: string; // the coach's personal number
  createdAt: number; // creation time (epoch ms)
}

/**
 * Enrols an athlete into a training class (the athlete belongs to the same
 * company). Only the exact (class, athlete) pair is unique among live rows;
 * a removed athlete can be re-enrolled. Deletion is soft.
 */
export interface TrainingClassEnrollment {
  id: number; // auto-assigned integer primary key
  trainingClassId: number;
  athleteId: string; // the enrolled athlete's personal number
  createdAt: number; // creation time (epoch ms)
}

/**
 * One dated occurrence of a training class — the unit attendance is tracked
 * against. `date` is an ISO `YYYY-MM-DD` calendar date; `note` is optional
 * free text (e.g. the session's focus). Deletion is soft.
 */
export interface TrainingSession {
  id: number; // auto-assigned integer primary key
  trainingClassId: number;
  date: string; // ISO calendar date, "YYYY-MM-DD"
  note: string; // free-text note ('' = none)
  createdAt: number; // creation time (epoch ms)
}

/** Whether an athlete showed up to a training session. */
export type AttendanceStatus = 'PRESENT' | 'ABSENT';

/**
 * A coach's attendance mark for one athlete at one session — exactly one row
 * per (session, athlete). Re-marking updates the row's `status` in place, so
 * (unlike the other training entities) attendance is not soft-deleted; a
 * record is surfaced only through its live session.
 */
export interface AttendanceRecord {
  id: number; // auto-assigned integer primary key
  trainingSessionId: number;
  athleteId: string; // the marked athlete's personal number
  status: AttendanceStatus;
  createdAt: number; // first-marked time (epoch ms)
}

/** The raw fields captured by the training-class form (create and edit). */
export interface TrainingClassInput {
  name: string;
  description?: string;
  // The weekly schedule. Typed as slots for the client; the server treats it
  // as unknown and validates each slot (weekday + `HH:MM` start/end).
  schedule?: TrainingSlot[];
  // Who the class is for. Typed as the code for the client; the server
  // validates it ('' / absent = 'ALL').
  audience?: TrainingAudience;
}

/** The raw fields captured when attaching a coach or enrolling an athlete. */
export interface TrainingClassMemberInput {
  athleteId?: string;
}

/** The raw fields captured by the training-session form (create only). */
export interface TrainingSessionInput {
  date: string;
  note?: string;
}

/** One athlete's attendance mark submitted when a coach saves a session. */
export interface AttendanceMarkInput {
  athleteId?: string;
  status?: string;
}

/** Stable, globally-unique player identity, reused across tournaments. */
export type PlayerKey = string; // e.g. "PLR_5f3c0b2e-..."

/**
 * A tournament participant. For an individual sport (table tennis) this is one
 * person; for a team sport (football) the "player" is a whole team and
 * `members` lists its squad. Either way it occupies one P1..Pn entry slot, so
 * the grouping / scheduling / bracket engines never care which kind it is.
 */
export interface Player {
  id: PlayerId; // "P1" — per-tournament seed/entry slot (named-block key)
  playerKey: PlayerKey; // stable id across tournaments — used for rating
  name: string; // real name, e.g. "Greg Smith" (or a team name, "FC Riverside")
  seed: number; // 1-based global entry index
  // Squad member names in list order for a team-sport participant; always []
  // for an individual sport. Edited manually while the tournament is a DRAFT.
  members: string[];
}

/** A roster entry supplied at tournament creation, in seed order. */
export interface PlayerInput {
  name: string;
  playerKey?: PlayerKey; // caller-generated; build falls back to a deterministic key
  members?: string[]; // team-sport squad names; ignored size/blank entries are normalized away
}

/**
 * A referee attached to a (football) tournament. Referees are entered manually
 * like roster entries, but play no part in any scoring or bracket algorithm —
 * they exist to be assigned to matches and printed on match-report forms.
 * Keyed by a named-block id (`Referee_1`, ...) that is never renumbered, so a
 * removal cannot silently re-point existing match assignments.
 */
export interface Referee {
  id: string; // "Referee_1" — named-block key, stable for the tournament's life
  name: string;
  seed: number; // 1-based insertion index (display order); also the id's number
}

/**
 * Review state of a public sign-up. A registration starts UNVERIFIED while the
 * registrant proves ownership of their mobile number with a one-time passcode;
 * once verified it becomes PENDING, and the organiser then either APPROVES it
 * (which adds a player to the draft roster) or REJECTS it. UNVERIFIED sign-ups
 * are never shown to the organiser — they are not yet a real entry request.
 */
export type RegistrationStatus = 'UNVERIFIED' | 'PENDING' | 'APPROVED' | 'REJECTED';

/**
 * A public sign-up submitted through the shareable QR link before the tournament
 * starts. Distinct from `Player`: registrations carry the registrant's personal
 * details (never exposed on the public read snapshot) and are reviewed by the
 * organiser. A fresh sign-up is UNVERIFIED until the registrant enters the OTP
 * delivered to their mobile (see `OtpChallenge`); it then becomes PENDING and
 * enters the organiser's review queue. Approving one adds a `Player` to the draft
 * roster, linked back via `playerKey`. `dateOfBirth` is an ISO `YYYY-MM-DD` date;
 * `mobile` is a normalized E.164 number (Cambodian `+855…` by default).
 */
export interface Registration {
  id: string; // Reg_<uuid>
  fullName: string;
  dateOfBirth: string; // ISO calendar date, "YYYY-MM-DD"
  mobile: string; // normalized E.164, e.g. "+85512345678"
  status: RegistrationStatus;
  playerKey: PlayerKey | null; // stable key of the roster player added on approval
  createdAt: number; // submission time (epoch ms)
}

/**
 * A one-time-passcode challenge guarding a sign-up's mobile number. Created
 * alongside an UNVERIFIED `Registration`: the plaintext code is delivered to the
 * registrant out-of-band (see the messaging topic) and only its `codeHash` is
 * stored, so a database leak never exposes a live code. The challenge is consumed
 * (deleted) on the first correct entry, which flips the registration to PENDING.
 * `attempts` bounds brute-force guessing and `expiresAt` bounds the code's life.
 */
export interface OtpChallenge {
  registrationId: string; // the UNVERIFIED registration this code unlocks
  codeHash: string; // keyed hash of the 6-digit code; never the code itself
  expiresAt: number; // code expiry (epoch ms)
  attempts: number; // failed verification attempts so far
  createdAt: number; // issue time (epoch ms)
}

/** The raw fields captured by the public registration form. */
export interface RegistrationInput {
  fullName: string;
  dateOfBirth: string;
  mobile: string;
}

/**
 * Processing state of an organiser-uploaded tournament photo.
 *   PROCESSING — the original JPEG is stored; the async pipeline (orient →
 *                resize → watermark → WebP) has not finished yet.
 *   READY      — processing finished and the photo is published: `webpUrl` is a
 *                public URL shown in the spectator gallery.
 *   FAILED     — processing failed (`error` explains why); never shown publicly.
 */
export type PhotoStatus = 'PROCESSING' | 'READY' | 'FAILED';

/** Aspect class of a processed photo (post-orientation), used to lay out the gallery. */
export type PhotoOrientation = 'LANDSCAPE' | 'PORTRAIT' | 'SQUARE';

/**
 * The full, server-internal representation of a tournament photo. `originalObject`
 * and `webpObject` are storage keys (GCS object names) — never exposed to any
 * client. The private original is kept but its URL is never published; only the
 * watermarked, mobile-sized WebP at `webpUrl` is public. Photos live OUTSIDE
 * `TournamentState` (like registrations): they survive `saveState` and are
 * cascade-deleted with the tournament. Map to the client DTOs below before
 * returning to a browser.
 */
export interface StoredPhoto {
  id: string; // Photo_<uuid>
  status: PhotoStatus;
  originalObject: string; // GCS key of the private original (server-only, never exposed)
  webpObject: string | null; // GCS key of the processed WebP; null until READY
  webpUrl: string | null; // public URL of the processed WebP; null until READY
  width: number | null; // processed pixel width (post-orientation)
  height: number | null; // processed pixel height (post-orientation)
  orientation: PhotoOrientation | null; // aspect class of the processed image
  comment: string; // organiser caption shown on the public page ("" when none)
  error: string | null; // failure reason when status is FAILED
  createdAt: number; // upload time (epoch ms)
}

/**
 * Organiser-facing view of a photo (the manager "Photos" tab). Carries the
 * processing status and caption but never the private storage keys.
 */
export interface TournamentPhoto {
  id: string;
  status: PhotoStatus;
  webpUrl: string | null; // public WebP URL once READY, else null
  width: number | null;
  height: number | null;
  orientation: PhotoOrientation | null;
  comment: string;
  error: string | null;
  createdAt: number;
}

/**
 * Public projection of a READY photo shown in the spectator gallery. Carries only
 * what the gallery needs to render an orientation-aware, captioned, downloadable
 * image — never the original, the storage keys, or any processing detail.
 */
export interface PublicPhoto {
  id: string;
  url: string; // public WebP URL
  width: number;
  height: number;
  orientation: PhotoOrientation;
  comment: string;
}

export type MatchStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';

/**
 * Tournament-level lifecycle status.
 *   DRAFT       — created via the wizard; the roster is still being edited and
 *                 the group stage / bracket have not been built yet.
 *   IN_PROGRESS — started; the full structure is built and scoring is allowed.
 *   FINISHED    — the bracket reached its champion; the tournament is locked.
 */
export type TournamentStatus = 'DRAFT' | 'IN_PROGRESS' | 'FINISHED';

/** The point score of a single game within a match's best-of-N series. */
export interface GameScore {
  pointsA: number;
  pointsB: number;
}

/** The colour of a football booking. */
export type FootballCardColor = 'YELLOW' | 'RED';

/**
 * One goal of a football match, credited to `side`'s score and attributed to a
 * squad member. `memberIndex` is the 0-based position in the scorer's team
 * roster (`Player.members`) — the roster of `side`, or of the *other* side for
 * an own goal. Index references are stable because squads are editable only
 * while the tournament is a DRAFT and events exist only after it starts.
 * Penalty shoot-out conversions are never goal events (see
 * `features/football-tournaments.md`).
 */
export interface FootballGoalEvent {
  kind: 'GOAL';
  side: 'A' | 'B'; // the team whose score the goal counts for
  memberIndex: number; // 0-based squad-list index of the scorer
  ownGoal: boolean; // scorer belongs to the OTHER side's roster
  minute: number | null; // match minute (1..FOOTBALL_MINUTE_MAX); null = unrecorded
}

/** A yellow or red card shown to one squad member of `side`'s team. */
export interface FootballCardEvent {
  kind: 'CARD';
  side: 'A' | 'B'; // the booked player's team
  memberIndex: number; // 0-based squad-list index of the booked player
  card: FootballCardColor;
  minute: number | null; // match minute (1..FOOTBALL_MINUTE_MAX); null = unrecorded
}

/**
 * A referee-tracked incident of a football match, in entry order. Events are
 * supplementary attribution on top of the authoritative numeric score and are
 * validated against it (`validateFootballEvents` in `src/domain/football.ts`).
 */
export type FootballMatchEvent = FootballGoalEvent | FootballCardEvent;

/**
 * A group-stage match. Rendered as a single cell in a pool's square matrix.
 * The match is a best-of-`bestOf` series; `games` holds the point score of each
 * game played, in order. Games won, total points and the winner are all derived
 * from `games` (see `summarizeMatch`) — never stored.
 */
export interface Cell {
  id: string; // Cell_PoolA_P1_P3
  poolId: string; // Pool_A
  poolLabel: string; // A
  playerA: PlayerId; // row player
  playerB: PlayerId; // column player
  bestOf: number; // series length (odd); defaults to config.groupBestOf
  games: GameScore[]; // per-game point scores, in play order
  // Referee-tracked goal/card events (football), in entry order; always []
  // for table tennis and cleared whenever the score is cleared.
  events: FootballMatchEvent[];
  status: MatchStatus;
  timeSlot: number; // 1-based Time_Slot index
  tableIndex: number; // 1-based table index within the time slot
  refereeId: string | null; // assigned referee (football); null = unassigned
}

export interface Group {
  id: string; // Pool_A
  label: string; // A
  playerIds: PlayerId[];
  cellIds: string[]; // round-robin matches, in generation order
}

export interface Standing {
  playerId: PlayerId;
  matchesPlayed: number;
  matchesWon: number;
  gamesWon: number;
  gamesLost: number;
  gamesDiff: number;
  pointsWon: number;
  pointsLost: number;
  pointsDiff: number;
  rank: number; // 1-based rank within the pool
}

/**
 * A pool standings row under FIFA rules (football): 3 points a win, 1 a draw,
 * ranked Points → Goal difference → Goals scored (see
 * `src/domain/football.ts`). The structural cousin of `Standing`; which of the
 * two a pool uses is decided by the tournament's `sportId`.
 */
export interface FootballStanding {
  playerId: PlayerId; // the team's entry slot ("P1")
  matchesPlayed: number;
  matchesWon: number;
  matchesDrawn: number;
  matchesLost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDiff: number;
  points: number; // 3 × won + 1 × drawn
  rank: number; // 1-based rank within the pool
}

/** A final-placement entry (gold/silver/bronze) on the tournament podium. */
export interface PodiumEntry {
  place: 1 | 2 | 3;
  playerId: PlayerId | null; // null until the deciding match resolves
}

/** Entry placeholder filled when a pool finishes. */
export interface BracketSlot {
  id: string; // Slot_PoolA_1st
  poolId: string; // Pool_A
  poolLabel: string; // A
  position: 1 | 2; // 1st / 2nd
  playerId: PlayerId | null; // resolved when the pool completes
}

/**
 * Reference to a participant of a bracket node. A participant is either a pool
 * slot (group qualifier), the winner of a previous node, the loser of a previous
 * node (used by the third-place playoff), or a structural bye.
 */
export type ParticipantRef =
  | { kind: 'slot'; slotId: string }
  | { kind: 'winner'; nodeId: string }
  | { kind: 'loser'; nodeId: string }
  | { kind: 'bye' };

export interface BracketNode {
  id: string; // Node_R1_M1
  round: number; // 0 = play-in pre-round, 1..K = main rounds
  matchIndex: number; // 1-based within the round
  a: ParticipantRef;
  b: ParticipantRef;
  playerA: PlayerId | null; // resolved participant
  playerB: PlayerId | null;
  bestOf: number; // series length (odd); config.bracketBestOf, or config.finalBestOf for the final-round games
  games: GameScore[]; // per-game point scores, in play order
  // Referee-tracked goal/card events (football), in entry order; always []
  // for table tennis and cleared whenever the score is cleared.
  events: FootballMatchEvent[];
  status: MatchStatus;
  winner: PlayerId | null;
  nextNodeId: string | null; // node the winner advances into
  nextSlot: 'a' | 'b' | null; // side of the next node the winner occupies
  label: string; // "Quarterfinal 1", "Final", "Play-in 1", ...
  refereeId: string | null; // assigned referee (football); null = unassigned
}

export interface TournamentConfig {
  totalPlayers: number;
  availableTables: number;
  groupBestOf?: number; // series length for group matches (default 3)
  bracketBestOf?: number; // series length for bracket matches (default 5)
  // Series length for the two final games — the Final and the third-place
  // playoff. Lets a long field keep short earlier rounds while still playing a
  // longer decider. Defaults to `bracketBestOf` when unset.
  finalBestOf?: number;
}

/** The full normalized tournament state, keyed by named-block string IDs. */
export interface TournamentState {
  id: string; // stable tournament id, e.g. "Tournament_<uuid>"
  companyId: number; // integer id of the company governing this tournament
  // The sport being played (from the catalog in `src/domain/sports.ts`), picked
  // at creation and immutable. The single discriminator between the
  // table-tennis best-of-N scoring/standings and the FIFA football rules.
  sportId: SportId;
  name: string; // human-readable label shown in the management list
  description: string; // free-text blurb shown on the public page before start
  coverUrl: string | null; // public URL of the cover image (GCS); null when none
  startsAt: number | null; // scheduled start (epoch ms); null when unscheduled
  acceptPublicRegistration: boolean; // whether the public QR link accepts sign-ups
  registrationClosesAt: number | null; // sign-up deadline (epoch ms); null = no deadline
  status: TournamentStatus;
  config: TournamentConfig;
  players: Record<PlayerId, Player>;
  groups: Record<string, Group>;
  cells: Record<string, Cell>;
  slots: Record<string, BracketSlot>;
  nodes: Record<string, BracketNode>;
  // The tournament's referees (football), keyed by `Referee_N` id. Empty for
  // sports without referees. Manageable while DRAFT or IN_PROGRESS.
  referees: Record<string, Referee>;
  // Highest referee number ever allocated for this tournament — the source of
  // the next `Referee_N` id. Never decremented, so a removed referee's id can
  // never be reissued to a different person (the map alone cannot know about
  // removed numbers; see `addReferee`).
  refereeSeq: number;
  groupOrder: string[]; // pool ids in display order
  nodeOrder: string[]; // node ids in display order (round then match)
  timeSlotCount: number;
  createdAt: number;
}

/** Lightweight tournament descriptor for the management list view. */
export interface TournamentSummary {
  id: string;
  companyId: number;
  sportId: SportId; // sport being played (catalog code, e.g. "FOOTBALL")
  name: string;
  status: TournamentStatus;
  totalPlayers: number;
  coverUrl: string | null; // public cover image URL; null when none
  acceptPublicRegistration: boolean; // whether the public QR link accepts sign-ups
  createdAt: number;
}

/** Fallback label when a tournament is created without an explicit name. */
export const DEFAULT_TOURNAMENT_NAME = 'Untitled tournament';

/**
 * Sport of every tournament created before the sport picker existed (and of any
 * creation that does not name one). Matches the `sportId` column default, so
 * pre-existing rows read back unchanged.
 */
export const DEFAULT_TOURNAMENT_SPORT: SportId = 'TABLE_TENNIS';

/** Max squad members a single team-sport participant may list. */
export const TEAM_MEMBER_MAX = 40;

/**
 * Integer id of the default company that governs tournaments created before
 * companies existed (and any tournament created without an explicit company).
 * The `company` migration seeds the Company table with this row first, so the
 * fresh auto-increment sequence deterministically assigns it id 1.
 */
export const DEFAULT_COMPANY_ID = 1;
/**
 * Display name of the company that pre-company tournaments were migrated onto.
 * The row itself is provisioned by the `company` migration and (idempotently)
 * at server boot.
 */
export const DEFAULT_COMPANY_NAME = 'Default Company';
/** Max length accepted for a company's display name. */
export const COMPANY_NAME_MAX = 120;
/** Max length accepted for a company's profile description (markdown source). */
export const COMPANY_DESCRIPTION_MAX = 4000;
/** Max number of profile photos a single company may hold. */
export const COMPANY_PHOTO_MAX = 10;
/** Max length accepted for a location's canonical name and each translation. */
export const LOCATION_NAME_MAX = 120;

export const PLAYER_MIN = 4;
// Upper bound on the field size. Raised well beyond the original 32 so larger
// events are supported; the grouping, scheduling and bracket algorithms are all
// size-agnostic, and pool labels scale past Z via `poolLabel` (A..Z, AA, AB..).
export const PLAYER_MAX = 128;

/** Max length accepted for a registrant's full name. */
export const REGISTRATION_NAME_MAX = 120;
/** Earliest birth year accepted on a registration (sanity floor). */
export const REGISTRATION_MIN_BIRTH_YEAR = 1900;
/** Default dialing code applied to a bare local mobile number (Cambodia). */
export const DEFAULT_DIAL_CODE = '+855';
export const TABLE_MIN = 1;
export const TABLE_MAX = 10;

/** Canonical square cover-image edge length (px). Covers are stored at this size. */
export const COVER_SIZE = 600;

/** Maximum number of photos a single tournament may hold (across all uploads). */
export const PHOTO_MAX_COUNT = 200;
/**
 * Longest edge (px) of a processed gallery image. Photos are downscaled to fit
 * within this box (never upscaled) so they load fast and render crisply on
 * mobile screens while staying large enough for fullscreen viewing.
 */
export const PHOTO_MAX_EDGE = 1600;
/**
 * Default text burned into the bottom-right corner of every processed photo. The
 * server can override it at runtime with `TAE_PHOTO_WATERMARK_TEXT`.
 */
export const PHOTO_WATERMARK_TEXT = 'Sponsored by Neary Skin';
/** Maximum length accepted for an organiser's per-photo caption. */
export const PHOTO_COMMENT_MAX = 280;

/** Default series lengths; both phases accept any odd N >= 1. */
export const DEFAULT_GROUP_BEST_OF = 3;
export const DEFAULT_BRACKET_BEST_OF = 5;
/** Odd series lengths offered in the config UI. */
export const BEST_OF_CHOICES = [1, 3, 5, 7, 9] as const;
